import { AccessToken } from '../utils';

/**
 * Creates a WebSocket URL based on the current environment and configuration
 * 
 * @param {string} path - WebSocket path (e.g. '/ws/quickbooks/sync/1234')
 * @param {boolean} includeToken - Whether to include the auth token
 * @returns {string} Complete WebSocket URL
 */
export const getWebSocketUrl = (path, includeToken = true) => {
  // Debug mode can be enabled with environment variable or localStorage
  const debug = process.env.REACT_APP_WS_DEBUG === 'true';
  
  // Ensure path starts with /ws/
  const wsPath = path.startsWith('/ws/') ? path : `/ws${path.startsWith('/') ? path : '/' + path}`;
  
  // Get token if needed
  const token = includeToken ? AccessToken() : null;
  const tokenParam = token ? `${wsPath.includes('?') ? '&' : '?'}token=${token}` : '';
  
  // Check for environment variable first
  const envWsUrl = process.env.REACT_APP_WS_URL;
  
  // Look for explicitly configured WebSocket endpoint
  const configuredWsEndpoint = envWsUrl; // || localStorage.getItem('tunnelWsUrl') || localStorage.getItem('wsEndpoint');
  
  // Check if we're on a secure connection
  const isSecure = window.location.protocol === 'https:';
  
  let wsUrl;
  
  if (configuredWsEndpoint) {
    // Use configured endpoint
    // First, determine the correct protocol
    let protocol;
    if (configuredWsEndpoint.startsWith('https:') || configuredWsEndpoint.startsWith('wss:')) {
      protocol = 'wss:';
    } else if (configuredWsEndpoint.startsWith('http:') || configuredWsEndpoint.startsWith('ws:')) {
      // If current page is secure but endpoint is not, upgrade to secure WebSocket
      protocol = isSecure ? 'wss:' : 'ws:';
    } else {
      // No protocol specified, match the page's security level
      protocol = isSecure ? 'wss:' : 'ws:';
    }
    
    // Extract the host, removing any protocol prefix
    const host = configuredWsEndpoint
      .replace(/^https?:\/\//, '')
      .replace(/^wss?:\/\//, '')
      .replace(/\/$/, ''); // Remove trailing slash
    
    wsUrl = `${protocol}//${host}${wsPath}${tokenParam}`;
    
    if (debug) console.log('Using configured WebSocket endpoint:', wsUrl);
  } else {
    // Default: use current page's domain
    const protocol = isSecure ? 'wss:' : 'ws:';
    const host = window.location.host;
    wsUrl = `${protocol}//${host}${wsPath}${tokenParam}`;
    
    // Special handling for Tailscale/tunneled domains
    if (host.includes('.ts.net') || host.includes('tail')) {
      if (debug) console.log('Detected Tailscale/tunnel domain:', host);
      
      // Check for overridden WebSocket URL for tunneled connections
      const tunnelWsUrl = localStorage.getItem('tunnelWsUrl');
      if (tunnelWsUrl) {
        try {
          // Parse the overridden URL
          const tunnelUrl = new URL(tunnelWsUrl);
          // Use the overridden URL base but ensure protocol matches security context
          const tunnelProtocol = isSecure ? 'wss:' : tunnelUrl.protocol.startsWith('wss') ? 'wss:' : 'ws:';
          wsUrl = `${tunnelProtocol}//${tunnelUrl.host}${wsPath}${tokenParam}`;
          if (debug) console.log('Using tunnel WebSocket URL override:', wsUrl);
        } catch (e) {
          // If URL parsing fails, use the tunnelWsUrl directly as a base
          wsUrl = `${tunnelWsUrl}${wsPath}${tokenParam}`;
          // Ensure protocol is secure if page is secure
          if (isSecure && wsUrl.startsWith('ws:')) {
            wsUrl = wsUrl.replace('ws:', 'wss:');
          }
          if (debug) console.log('Using tunnel WebSocket URL (fallback):', wsUrl);
        }
      }
    }
  }
  
  return wsUrl;
};
